import React from 'react';
import PropTypes from 'prop-types';
import { ONBOARD_STEPS as STEPS, useOnboardWizard } from '../../hooks/useAdminForms';
import { COPY } from '../../utils/adminConsole';
import DataSourceFields, { DEFAULT_DATABASE } from './DataSourceFields';
import DataSourceCheck, { TenantStatusBadge } from './DataSourceCheck';

// Guided dealer onboarding — the org type is derived from plain questions
// (part of a holding?), never a raw picker. Creates the dealer (+ holding)
// and invites the first admin in one call, then — because that call is what
// hands us the new org id — connects the dealer's data source and lets the
// operator verify and switch it on without leaving the wizard.
// Logic: hooks/useAdminForms (shared with the redesign).
function OnboardWizard({ orgs, onClose, onDone }) {
    const {
        holdings, step, setStep, dealerName, onName, effSlug, onSlug, inHolding, setInHolding, holdingChoice, setHoldingChoice,
        holdingName, setHoldingName, adminEmail, setAdminEmail, adminName, setAdminName, saving, error, source, setSource,
        created, tenant, check, busy, hasEndpoint, blocked, canNext, close, connect, submit, runCheck, activate,
    } = useOnboardWizard({ orgs, onClose, onDone });

    return (
        <div className="ac-modal-backdrop" onClick={close}>
            <div className="ac-modal ac-wizard" onClick={(e) => e.stopPropagation()}>
                <div className="ac-modal-head">
                    <h2>Onboard a dealership</h2>
                    <button className="ac-modal-x" onClick={close} aria-label="Close">×</button>
                </div>

                <div className="ac-stepper">
                    {STEPS.map((s, i) => (
                        <div key={s} className={`ac-step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
                            <span className="ac-step-num">{i < step ? '✓' : i + 1}</span>
                            <span className="ac-step-label">{s}</span>
                        </div>
                    ))}
                </div>
                <div className="ac-progress"><div className="ac-progress-bar" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} /></div>

                <div className="ac-wizard-body">
                    {step === 0 && (
                        <>
                            <label className="ac-field">
                                <span>Dealership name</span>
                                <input type="text" autoFocus placeholder="e.g. Midwest Equipment Co."
                                    value={dealerName} onChange={(e) => onName(e.target.value)} />
                            </label>
                            <label className="ac-field">
                                <span>Sign-in address</span>
                                <div className="ac-slug-row">
                                    <span className="ac-slug-prefix">/login/</span>
                                    <input type="text" value={effSlug}
                                        onChange={(e) => onSlug(e.target.value)} />
                                </div>
                            </label>
                            <p className="ac-modal-hint">The dealer’s people sign in at this workspace address.</p>
                        </>
                    )}

                    {step === 1 && (
                        <>
                            <div className="ac-field">
                                <span>Is this dealership part of a holding group?</span>
                                <div className="ac-choices">
                                    <button className={!inHolding ? 'on' : ''} onClick={() => setInHolding(false)}>No — standalone</button>
                                    <button className={inHolding ? 'on' : ''} onClick={() => setInHolding(true)}>Yes, it’s part of a group</button>
                                </div>
                            </div>
                            {inHolding && (
                                <label className="ac-field">
                                    <span>Holding group</span>
                                    <select value={holdingChoice} onChange={(e) => setHoldingChoice(e.target.value)}>
                                        <option value="">Select a holding…</option>
                                        {holdings.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                                        <option value="__new__">+ Create a new holding…</option>
                                    </select>
                                    {holdingChoice === '__new__' && (
                                        <input type="text" style={{ marginTop: '0.5rem' }} placeholder="Holding name (e.g. Brand Holdings)"
                                            value={holdingName} onChange={(e) => setHoldingName(e.target.value)} />
                                    )}
                                </label>
                            )}
                        </>
                    )}

                    {step === 2 && (
                        <>
                            <p className="ac-modal-hint">Invite the person who’ll run this dealership — they become its admin and can add their own team. You can skip and do it later.</p>
                            <label className="ac-field">
                                <span>Admin email</span>
                                <input type="email" placeholder="name@dealer.com"
                                    value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} />
                            </label>
                            <label className="ac-field">
                                <span>Name (optional)</span>
                                <input type="text" placeholder="Full name"
                                    value={adminName} onChange={(e) => setAdminName(e.target.value)} />
                            </label>
                            {adminEmail.trim() && <p className="ac-modal-hint">{COPY.onboardAdminHint}</p>}
                        </>
                    )}

                    {step === 3 && !created && (
                        <>
                            <p className="ac-modal-hint">
                                Where should IronSight read this dealership’s data from? The data team builds and
                                registers the warehouse — paste the endpoint they gave you and we’ll connect to it.
                            </p>
                            <DataSourceFields value={source} onChange={setSource} disabled={saving} />
                        </>
                    )}

                    {step === 3 && created && (
                        <div className="ac-ds-done">
                            <p className="ac-ds-created">
                                <span className="ac-ds-tick" aria-hidden="true">✓</span>
                                <span>
                                    <strong>{created.dealer?.name || dealerName}</strong> created
                                    {created.admin ? <> · <strong>{created.admin.email}</strong> invited as its admin</> : null}.
                                </span>
                            </p>

                            {tenant ? (
                                <>
                                    <div className="ac-ds-summary">
                                        <div className="ac-ds-mono">{tenant.synapse_server}</div>
                                        <div className="ac-ds-sub">
                                            {tenant.database || DEFAULT_DATABASE}
                                            {tenant.lake_account ? ` · ${tenant.lake_account}` : ' · no storage account'}
                                        </div>
                                        <div className="ac-ds-summary-status">
                                            <TenantStatusBadge status={tenant.status} />
                                            {tenant.is_demo && <span className="ac-ds-demo">Demo</span>}
                                        </div>
                                    </div>

                                    <div className="ac-ds-actions">
                                        <button className="ac-act" disabled={!!busy} onClick={runCheck}>
                                            {busy === 'check' ? 'Testing…' : 'Test connection'}
                                        </button>
                                        <button
                                            className="ac-act"
                                            disabled={!!busy || blocked || tenant.status === 'active'}
                                            title={blocked ? 'Resolve the failing checks first.' : undefined}
                                            onClick={activate}
                                        >
                                            {busy === 'activate' ? 'Activating…' : tenant.status === 'active' ? 'Active' : 'Activate'}
                                        </button>
                                    </div>

                                    {busy === 'check' || busy === 'activate' ? (
                                        <span className="ac-ds-pending">
                                            <span className="ac-ds-spinner" aria-hidden="true" />
                                            Querying the dealer database — this can take a few seconds.
                                        </span>
                                    ) : (
                                        <DataSourceCheck result={check} />
                                    )}
                                </>
                            ) : (
                                <>
                                    <p className="ac-modal-hint">
                                        No data source is connected yet. You can attach one any time from
                                        <strong> Data sources</strong> — the dealership just stays empty until then.
                                    </p>
                                    {hasEndpoint && (
                                        <button className="ac-act" disabled={busy === 'connect'}
                                            onClick={() => connect(created.dealer?.id, created.dealer?.slug)}>
                                            {busy === 'connect' ? 'Connecting…' : 'Retry connecting the source'}
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {error && <div className="ac-error">{error}</div>}
                </div>

                <div className="ac-modal-actions ac-wizard-actions">
                    {created ? (
                        <>
                            <span />
                            <button className="ac-btn-primary" onClick={close} disabled={!!busy}>Done</button>
                        </>
                    ) : (
                        <>
                            <button className="ac-btn-ghost" onClick={step === 0 ? close : () => setStep(s => s - 1)} disabled={saving}>
                                {step === 0 ? 'Cancel' : 'Back'}
                            </button>
                            {step < STEPS.length - 1 ? (
                                <button className="ac-btn-primary" onClick={() => setStep(s => s + 1)} disabled={!canNext}>Next</button>
                            ) : (
                                <div className="ac-ds-submit">
                                    <button className="ac-add-inline" onClick={() => submit(false)} disabled={saving}>
                                        Skip — connect the source later
                                    </button>
                                    <button className="ac-btn-primary" onClick={() => submit(true)} disabled={saving || !hasEndpoint}>
                                        {saving ? 'Creating…' : 'Create & connect source'}
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

OnboardWizard.propTypes = {
    orgs: PropTypes.array.isRequired,
    onClose: PropTypes.func.isRequired,
    onDone: PropTypes.func.isRequired,
};

export default OnboardWizard;
